import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import type { StreamContextType, StreamBlock, AIToolData } from './Types';
import { useStreamContext } from './StreamContext';
import { setStreamUIState, getStreamUIState, setPendingTextBoundary, setPendingTextBoundaryForKey, takeAndClearPendingBoundaryForKey, registerAgentKey, beginActiveRound, addKeyToActiveRound, markRoundKeyComplete, clearActiveRound, getResponseGroupForKey, bumpResponseGroupForKey, resetTurnState, registerTaskToolForKey, upsertToolVm, setActiveSession, setCurrentTurnId, getCurrentTurnId } from './streamStore';
import { resetSubAgentColorAssignments } from './subAgent';
import { getToolDef, isHiddenToolName, isKnownToolName } from './Tools';
import { DebugStream } from '../internal/DebugStream';
import { streamLog } from './streamLogger';
import { defaultConfig } from '../../../../../utils/config';

interface StreamStateProviderProps {
  config: any;
  isActive: boolean;
  onStreamContext?: (context: StreamContextType) => void;
}

const StreamStateProvider: React.FC<StreamStateProviderProps> = ({ config, isActive, onStreamContext }) => {
  const { setStreamContext: setCtxInProvider } = useStreamContext();
  const debugInstanceRef = useRef<DebugStream | null>(null);
  const debugStartedRef = useRef<boolean>(false);

  const generateBlockId = useCallback(() => {
    return `block_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const addTextContent = useCallback((content: string, meta?: { task_tool_id?: string; phase?: string; turn_id?: string }) => {
    if (!content) return;
    // Preserve whitespace-only chunks by appending to the last text block (do not create a new block)
    const isWhitespaceOnly = typeof content === 'string' && content.replace(/[\s\u200B\u00A0]+/g, '').length === 0;
    // Determine key used for boundary grouping
    const incomingKey = (meta?.task_tool_id) as string | undefined;
    const groupKey = incomingKey || '__MAIN__';
    // Turn rollover guard: if backend supplies a new turn_id, reset per-turn UI state
    try {
      const incomingTurnId = (meta as any)?.turn_id as string | undefined;
      const currentTurnId = getCurrentTurnId();
      if (incomingTurnId && incomingTurnId !== currentTurnId) {
        setCurrentTurnId(incomingTurnId);
        resetTurnState();
        if (incomingKey) setPendingTextBoundaryForKey(incomingKey, true); else setPendingTextBoundary(true);
      }
    } catch (_) {}
    // If a text boundary is pending for this key (between AI responses), prefix a double newline once
    let forceNewGroup = false;
    try {
      const boundaryForKey = takeAndClearPendingBoundaryForKey(groupKey);
      if (boundaryForKey) {
        content = `\n\n${content}`;
        forceNewGroup = true;
      } else {
        const s = getStreamUIState() as any;
        if (s.pendingTextBoundary && groupKey === '__MAIN__') {
          content = `\n\n${content}`;
          setPendingTextBoundary(false);
          forceNewGroup = true;
        }
      }
    } catch (_) {}
    const prev = getStreamUIState().content;
    const lastBlock = prev.blocks[prev.blocks.length - 1];
    // incomingKey already computed
    // Begin or accrete active round on keyed content
    try {
      if (incomingKey) {
        beginActiveRound(incomingKey);
        addKeyToActiveRound(incomingKey);
        // Ensure column order is stabilized as soon as we see text for this key
        registerAgentKey(incomingKey);
      }
    } catch (_) {}

    // Determine response group for this key (bump when boundary observed)
    let responseGroup = 0;
    try {
      if (forceNewGroup) bumpResponseGroupForKey(groupKey);
      responseGroup = getResponseGroupForKey(groupKey);
    } catch (_) {}

    const canAppendToLast = !!(
      lastBlock &&
      lastBlock.type === 'text' &&
      typeof lastBlock.content === 'string' &&
      (lastBlock as any).agentKey === incomingKey &&
      ((lastBlock as any).responseGroup ?? 0) === responseGroup
    );

    // Promotion: if the last block is a fresh unkeyed text block and we now have an incoming key,
    // upgrade that last block to carry the key and append this content to avoid transient unkeyed blocks.
    const canPromoteLast = !!(
      lastBlock &&
      lastBlock.type === 'text' &&
      typeof lastBlock.content === 'string' &&
      (lastBlock as any).agentKey == null &&
      incomingKey
    );
    const isLastFresh = !!(lastBlock && typeof (lastBlock as any).timestamp === 'number' && (Date.now() - (lastBlock as any).timestamp) < 1200);
    if (canPromoteLast && isLastFresh) {
      const updatedLastBlock: StreamBlock = { ...(lastBlock as any), agentKey: incomingKey, responseGroup, content: (lastBlock as any).content + content };
      const updatedBlocks = [...prev.blocks];
      updatedBlocks[updatedBlocks.length - 1] = updatedLastBlock;
      setStreamUIState({
        content: {
          ...prev,
          blocks: updatedBlocks,
          currentPosition: prev.currentPosition + content.length,
          totalLength: prev.totalLength + content.length,
        },
      });
      return;
    }

    if (isWhitespaceOnly) {
      if (canAppendToLast) {
        const updatedLastBlock: StreamBlock = { ...lastBlock, content: lastBlock.content + content };
        const updatedBlocks = [...prev.blocks];
        updatedBlocks[updatedBlocks.length - 1] = updatedLastBlock;
        setStreamUIState({
          content: {
            ...prev,
            blocks: updatedBlocks,
            currentPosition: prev.currentPosition + content.length,
            totalLength: prev.totalLength + content.length,
          },
        });
        return;
      }
      // Backward-append whitespace to latest matching key+group block; do not create a new whitespace block
      try {
        const idx = (() => {
          for (let i = prev.blocks.length - 1; i >= 0; i--) {
            const b = prev.blocks[i];
            if (b.type !== 'text' || typeof b.content !== 'string') continue;
            if ((b as any).agentKey === incomingKey && ((b as any).responseGroup ?? 0) === responseGroup) return i;
          }
          return -1;
        })();
        if (idx !== -1) {
          const target = prev.blocks[idx] as StreamBlock;
          const updatedBlocks = [...prev.blocks];
          updatedBlocks[idx] = { ...target, content: String(target.content) + content } as StreamBlock;
          setStreamUIState({
            content: {
              ...prev,
              blocks: updatedBlocks,
              currentPosition: prev.currentPosition + content.length,
              totalLength: prev.totalLength + content.length,
            },
          });
        }
      } catch (_) {}
      return;
    }

    if (canAppendToLast) {
      const updatedLastBlock: StreamBlock = { ...lastBlock, content: lastBlock.content + content };
      const updatedBlocks = [...prev.blocks];
      updatedBlocks[updatedBlocks.length - 1] = updatedLastBlock;
      setStreamUIState({
        content: {
          ...prev,
          blocks: updatedBlocks,
          currentPosition: prev.currentPosition + content.length,
          totalLength: prev.totalLength + content.length,
        },
      });
      return;
    }
    // Backward-append to the most recent matching key+group block; if none found, create a new block
    try {
      const idx = (() => {
        for (let i = prev.blocks.length - 1; i >= 0; i--) {
          const b = prev.blocks[i];
          if (b.type !== 'text' || typeof b.content !== 'string') continue;
          if ((b as any).agentKey === incomingKey && ((b as any).responseGroup ?? 0) === responseGroup) return i;
        }
        return -1;
      })();
      if (idx !== -1) {
        const target = prev.blocks[idx] as StreamBlock;
        const updatedBlocks = [...prev.blocks];
        updatedBlocks[idx] = { ...target, content: String(target.content) + content } as StreamBlock;
        setStreamUIState({
          content: {
            ...prev,
            blocks: updatedBlocks,
            currentPosition: prev.currentPosition + content.length,
            totalLength: prev.totalLength + content.length,
          },
        });
        return;
      }
    } catch (_) {}
    const newBlock: StreamBlock = {
      id: generateBlockId(),
      type: 'text',
      content,
      timestamp: Date.now(),
      position: prev.currentPosition,
      agentKey: incomingKey,
      responseGroup,
    };
    {
      const updatedBlocks = [...prev.blocks, newBlock];
      // Lift the matching task tool (if any) to immediately precede this first keyed block
      try {
        const targetIdx = updatedBlocks.length - 1;
        const liftKey = incomingKey ? String(incomingKey) : '';
        if (liftKey) {
          for (let i = targetIdx - 1; i >= 0; i--) {
            const b = updatedBlocks[i];
            if (b.type !== 'tool') continue;
            const t: any = b.content as any;
            if (t?.name !== 'task') continue;
            const p: any = t?.params || {};
            const fromMeta = p?.meta && (p.meta.task_tool_id);
            const key = String(fromMeta || t?.id || '');
            const pinned = t?._pinnedToColumn === true;
            if (!pinned && key && key === liftKey) {
              updatedBlocks.splice(i, 1);
              const insertAt = Math.max(0, targetIdx - 0);
              updatedBlocks.splice(insertAt, 0, { ...b, content: { ...t, _pinnedToColumn: true } });
              break;
            }
          }
        }
      } catch (_) {}
      setStreamUIState({
        content: {
          ...prev,
          blocks: updatedBlocks,
          currentPosition: prev.currentPosition + content.length,
          totalLength: prev.totalLength + content.length,
        },
      });
    }
    // Backfill the most recent task tool with task_tool_id if missing so it shares color with sub-agent
    try {
      const key = meta?.task_tool_id as string | undefined;
      if (key) {
        const s = getStreamUIState();
        for (let i = s.content.blocks.length - 1; i >= 0; i--) {
          const b = s.content.blocks[i];
          if (b.type === 'tool') {
            const t = (b.content as any);
            if (t?.name === 'task') {
              const hasKey = !!(t?.params?.meta?.task_tool_id);
              if (!hasKey) {
                const updated = { ...t, params: { ...(t.params || {}), meta: { ...((t.params || {}).meta || {}), task_tool_id: key } } };
                const updatedBlocks = [...s.content.blocks];
                updatedBlocks[i] = { ...b, content: updated };
                setStreamUIState({ content: { ...s.content, blocks: updatedBlocks } });
              }
              break;
            }
          }
        }
      }
    } catch (_) {}
  }, [generateBlockId]);

  const addTool = useCallback((tool: AIToolData) => {
    const prev = getStreamUIState().content;
    const known = isKnownToolName(tool.name as string);
    if (!known && !config?.DEBUG_STREAM_TOOLS) {
      try { streamLog.tool('[UI][tool] filtered unknown tool:', tool.name); } catch (_) {}
      return;
    }
    // Turn rollover guard using tool params.meta.turn_id when present
    try {
      const p: any = (tool as any)?.params || {};
      const meta: any = (p?.meta || {});
      const incomingTurnId = typeof meta?.turn_id === 'string' ? String(meta.turn_id) : undefined;
      const currentTurnId = getCurrentTurnId();
      if (incomingTurnId && incomingTurnId !== currentTurnId) {
        setCurrentTurnId(incomingTurnId);
        resetTurnState();
        setPendingTextBoundary(true);
      }
    } catch (_) {}
    // Defensive: drop umbrella task tool when parallel sub-agents are active and no key present
    try {
      if (defaultConfig.STREAM_PARALLEL_SUBAGENTS && (tool as any)?.name === 'task') {
        const p: any = (tool as any)?.params || {};
        const meta = (p?.meta || {}) as any;
        const hasKey = typeof meta?.task_tool_id === 'string';
        if (!hasKey) {
          return;
        }
      }
    } catch (_) {}
    // Skip rendering for hidden tools (e.g., stop_tool), but don't warn
    if (isHiddenToolName(tool.name as string)) {
      return;
    }
    // For 'task' tools: deduplicate by sub-agent key as well (dupes can arrive with different ids)
    try {
      const isTask = (tool as any)?.name === 'task';
      if (isTask) {
        const resolveTaskKey = (t: any): string | undefined => {
          const p: any = (t?.params || {});
          const fromMeta = p?.meta && (p.meta.task_tool_id);
          const direct = p?.task_tool_id;
          return String(fromMeta || direct || t?.id || '');
        };
        const incomingKey = resolveTaskKey(tool as any);
        if (incomingKey) {
          const existingIndexByKey = prev.blocks.findIndex(b => {
            if (b.type !== 'tool') return false;
            const t: any = b.content as any;
            if (t?.name !== 'task') return false;
            const k = resolveTaskKey(t);
            return k && String(k) === String(incomingKey);
          });
          if (existingIndexByKey !== -1) {
            const existing = prev.blocks[existingIndexByKey];
            const existingTool = existing.content as AIToolData;
            const merged: AIToolData = {
              ...existingTool,
              ...tool,
              startTime: existingTool.startTime || (tool as any).startTime,
            } as any;
            const updatedBlocks = [...prev.blocks];
            updatedBlocks[existingIndexByKey] = { ...existing, content: merged };
            try { upsertToolVm(merged as any); } catch (_) {}
            try { registerTaskToolForKey(String(incomingKey), String((merged as any).id)); } catch (_) {}
            setStreamUIState({ content: { ...prev, blocks: updatedBlocks } });
            return;
          }
        }
      }
    } catch (_) {}

    // Deduplicate: if a tool block with the same id already exists, merge/update instead of adding
    const existingIndex = prev.blocks.findIndex(b => b.type === 'tool' && (b.content as AIToolData).id === tool.id);
    if (existingIndex !== -1) {
      const existing = prev.blocks[existingIndex];
      const existingTool = existing.content as AIToolData;
      const mergedTool: AIToolData = {
        ...existingTool,
        // Preserve earliest startTime
        startTime: existingTool.startTime || tool.startTime,
        // Ensure status stays at least 'called'
        status: existingTool.status || 'called',
        // Merge params.args if provided
        params: (tool as any)?.params ? { ...(existingTool as any).params, ...(tool as any).params } : (existingTool as any).params,
        // Keep other display fields refreshed from latest config
        icon: tool.icon || existingTool.icon,
        textInProgress: tool.textInProgress || existingTool.textInProgress,
        textCompleted: tool.textCompleted || existingTool.textCompleted,
        label: tool.label || existingTool.label,
      } as any;
      const updatedBlocks = [...prev.blocks];
      updatedBlocks[existingIndex] = { ...existing, content: mergedTool };
      try { streamLog.warn('[UI][tool] merge duplicate call', tool.name, tool.id); } catch (_) {}
      setStreamUIState({ content: { ...prev, blocks: updatedBlocks } });
      return;
    }
    const newBlock: StreamBlock = {
      id: generateBlockId(),
      type: 'tool',
      content: {
        ...tool,
        ...(known ? {} : { textInProgress: tool.textInProgress || 'Running...', textCompleted: tool.textCompleted || 'Complete' })
      } as AIToolData,
      timestamp: Date.now(),
      position: prev.currentPosition,
    };
    // removed commented log
    // Backfill latest task tool with task_tool_id if this tool belongs to a sub-agent and the task is missing it
    try {
      const p: any = (newBlock.content as any)?.params || {};
      const fromMeta = p?.meta && (p.meta.task_tool_id);
      const fromResultMeta = p?.result?.meta && (p.result.meta.task_tool_id);
      const direct = p.task_tool_id as string | undefined;
      const subKey = (fromMeta || fromResultMeta || direct) as string | undefined;
      const name = (newBlock.content as any)?.name;
      const agentOnly = new Set(['merge_code']);
      // Do not register here for non-task tools
      // Index VM for live header updates
      try { upsertToolVm(newBlock.content as any); } catch (_) {}
      // Begin or accrete active round on keyed tool blocks
      try {
        if (subKey && !agentOnly.has(name)) {
          beginActiveRound(String(subKey));
          addKeyToActiveRound(String(subKey));
        }
      } catch (_) {}
      // If this is the task tool itself, register its id for stable ordering
      try {
        if ((newBlock.content as any)?.name === 'task' && typeof (newBlock.content as any)?.id === 'string') {
          const keyForTask = String(subKey || (newBlock.content as any).id);
          registerAgentKey(keyForTask);
          registerTaskToolForKey(keyForTask, String((newBlock.content as any).id));
        }
      } catch (_) {}
      if (subKey && (newBlock.content as any)?.name !== 'task' && !agentOnly.has(name)) {
        const updatedBlocks = [...prev.blocks, newBlock];
        for (let i = updatedBlocks.length - 2; i >= 0; i--) {
          const b = updatedBlocks[i];
          if (b.type === 'tool') {
            const t = (b.content as any);
            if (t?.name === 'task') {
              const hasKey = !!(t?.params?.meta?.task_tool_id);
              if (!hasKey) {
                const patched = { ...t, params: { ...(t.params || {}), meta: { ...((t.params || {}).meta || {}), task_tool_id: String(subKey) } } };
                updatedBlocks[i] = { ...b, content: patched };
              }
              break;
            }
          }
        }
        setStreamUIState({ content: { ...prev, blocks: updatedBlocks } });
        return;
      }
    } catch (_) {}
    setStreamUIState({ content: { ...prev, blocks: [...prev.blocks, newBlock] } });
  }, [config?.DEBUG_STREAM_TOOLS, generateBlockId]);

  const updateTool = useCallback((toolId: string, updates: Partial<AIToolData> | ((existing: AIToolData) => Partial<AIToolData>)) => {
    const prev = getStreamUIState().content;
    const updatedBlocks = prev.blocks.map(block => {
      if (block.type === 'tool') {
        const toolData = block.content as AIToolData;
        if (toolData.id === toolId) {
          const resolvedUpdates = typeof updates === 'function' ? updates(toolData) : updates;
          // removed commented log
          const updatedTool = {
            ...toolData,
            ...resolvedUpdates,
            params: (resolvedUpdates as any)?.params && typeof (resolvedUpdates as any).params === 'function'
              ? (resolvedUpdates as any).params((toolData as any).params)
              : (resolvedUpdates as any)?.params || (toolData as any).params,
            endTime: (resolvedUpdates as any)?.status === 'complete' ? Date.now() : toolData.endTime,
          } as AIToolData;
          return { ...block, content: updatedTool };
        }
      }
      return block;
    });
    setStreamUIState({ content: { ...prev, blocks: updatedBlocks } });
    try {
      const updatedBlock = updatedBlocks.find(b => b.type === 'tool' && (b.content as AIToolData).id === toolId);
      if (updatedBlock) upsertToolVm(updatedBlock.content as any);
    } catch (_) {}
    // If a tool completed and we can resolve its sub-agent key, mark it done in the active round
    try {
      const updatedBlock = updatedBlocks.find(b => b.type === 'tool' && (b.content as AIToolData).id === toolId);
      const updatedTool = updatedBlock?.content as any;
      if (updatedTool && updatedTool.status === 'complete') {
      const p: any = updatedTool?.params || {};
        const fromMeta = p?.meta && (p.meta.task_tool_id);
        const subKey = (fromMeta) as string | undefined;
        if (subKey) markRoundKeyComplete(String(subKey));
      }
    } catch (_) {}
  }, []);

  const clearContent = useCallback(() => {
    try {
      const s = getStreamUIState();
      try { streamLog.ctx('[UI][clearContent]', { totalLength: s.content.totalLength, isStreaming: s.isStreaming }); } catch (_) {}
    } catch (_) {}
    setStreamUIState({
      content: { blocks: [], currentPosition: 0, totalLength: 0 },
    });
  }, []);

  const streamContext = useMemo<StreamContextType>(() => ({
    addTextContent,
    addTool,
    updateTool,
    setIsStreaming: (streaming: boolean) => {
      try { streamLog.phase('🌊 Stream status:', streaming ? 'ACTIVE' : 'INACTIVE'); } catch (_) {}
      setStreamUIState({ isStreaming: streaming });
      if (streaming) {
        try { resetTurnState(); resetSubAgentColorAssignments(); } catch (_) {}
      }
    },
    setStreamPhase: (phase: string) => {
      try { streamLog.phase('🌊 Stream phase:', phase); } catch (_) {}
      setStreamUIState({ streamPhase: phase });
    },
    clearContent,
  }), [addTextContent, addTool, updateTool, clearContent]);

  useEffect(() => {
    setCtxInProvider(streamContext);
    onStreamContext?.(streamContext);
  }, [streamContext, setCtxInProvider, onStreamContext]);

  // Front-end only debug stream simulation (no backend call)
  useEffect(() => {
    if (!config?.DEBUG_STREAM || !config?.ENABLE_AI_STREAM) return;
    if (!isActive) return;
    if (debugStartedRef.current) return;

    debugStartedRef.current = true;
    const dbg = new DebugStream();
    debugInstanceRef.current = dbg;
    try {
      streamContext.clearContent();
      streamContext.setIsStreaming(true);
      try { setActiveSession('debug'); } catch (_) {}
      dbg.simulate({
        onMetadata: (data: any) => {
          try {
            if (data?.type === 'phase_transition' && data?.to) {
              streamContext.setStreamPhase?.(data.to);
            }
            // Respect AI response boundaries to start a new text section like live stream
            if (data?.type === 'ai_response_boundary') {
              const meta = (data as any)?.meta || {};
              const key = typeof (meta as any)?.task_tool_id === 'string' ? String((meta as any).task_tool_id) : '__MAIN__';
              if (key && key !== '__MAIN__') {
                setPendingTextBoundaryForKey(key, true);
              } else {
                setPendingTextBoundary(true);
              }
            }
          } catch (_) {}
        },
        onToolCall: (data: any) => {
          const id = data.toolId || data.id || `tool_${Date.now()}`;
          const name = data.toolName || data.type || 'Tool';
          if (isHiddenToolName(name as string)) {
            return; // Always suppress hidden tools in debug sim
          }
          // Keep simulator consistent: in non-debug, suppress unknowns (though sim usually runs in debug)
          try {
            if (!defaultConfig.DEBUG_STREAM_TOOLS) {
              const known = isKnownToolName(name as string);
              if (!known) return;
            }
          } catch (_) {}
          const args = (data && typeof data === 'object') ? (data.args || {}) : {};
          const meta = (data && typeof data === 'object') ? { ...(data.meta || {}), ...(data.phase ? { phase: data.phase } : {}) } : {};
          const def = getToolDef(name);
          const vm = def.getInitial({ id, name, params: { args, meta }, startTime: Date.now() });
          streamContext.addTool(vm as any);
        },
        onToolResult: (data: any) => {
          const id = data.toolId || data.id || `tool_${Date.now()}`;
          const name = data.toolName || data.type || data.name || 'Tool';
          if (isHiddenToolName(name as string)) {
            return; // Always suppress hidden tools in debug sim
          }
          try {
            if (!defaultConfig.DEBUG_STREAM_TOOLS) {
              const known = isKnownToolName(name as string);
              if (!known) return;
            }
          } catch (_) {}
          const result = (data && typeof data === 'object') ? (data.result || {}) : {};
          const meta = (data && typeof data === 'object') ? (data.meta || {}) : {};
          const def = getToolDef(name);
          streamContext.updateTool(id, (existing: any) => def.updateWithResult(existing, { ...result, meta }));
        },
        onAIContent: (data: any) => {
          const chunk: string = typeof data?.content === 'string' ? data.content : (typeof data?.deltaContent === 'string' ? data.deltaContent : '');
          if (chunk) {
            const meta = (data && typeof data === 'object') ? ((data as any).meta || {}) : {};
            if (data && typeof data === 'object' && (data as any).phase) {
              (meta as any).phase = (data as any).phase;
            }
            streamContext.addTextContent(chunk, meta);
          }
        },
        onComplete: (_data: any) => {},
        onDone: () => {
          streamContext.setIsStreaming(false);
          try { setActiveSession(null); } catch (_) {}
        },
        onError: (_error: Error) => {
          streamContext.setIsStreaming(false);
          try { setActiveSession(null); } catch (_) {}
        },
      });
    } catch (_e) {
      streamContext.setIsStreaming(false);
    }

    return () => {
      try { debugInstanceRef.current?.stop(); } catch (_) {}
      try { setActiveSession(null); } catch (_) {}
      debugInstanceRef.current = null;
    };
  }, [config?.DEBUG_STREAM, config?.ENABLE_AI_STREAM, isActive, streamContext]);

  return null;
};

export default StreamStateProvider;


